/**
 * Service layer — the single place the UI reads and writes data.
 * ---------------------------------------------------------------------------
 * Backed by Cloud Firestore. Public catalogue content is read directly from
 * Firestore with the web SDK: there is no API server in the request path, so
 * reads are fast, cached on the device, and cost a fraction of a function call.
 * Access is enforced by `firestore.rules` at the root of this repo.
 *
 * Writes (enquiry + contact form) go through callable Cloud Functions instead,
 * because they need server-side validation, spam protection and email.
 *
 * Every read is wrapped in `safe()`: if Firebase is unconfigured or unreachable
 * the bundled default content in `src/data/` is returned instead. The site is a
 * shop window — it must never render blank because a database call failed.
 */

import { getDocs, getDoc } from "firebase/firestore";
import { isFirebaseConfigured } from "@/lib/firebase";

export { isFirebaseConfigured };

// ---------------------------------------------------------------- doc mapping

/** Firestore Timestamp | ISO string | Date -> ISO date string (YYYY-MM-DD). */
export const toDateString = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value.slice(0, 10);
  const date = typeof value.toDate === "function" ? value.toDate() : new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toISOString().slice(0, 10);
};

/** Firestore Timestamp -> ISO datetime string, for admin tables. */
export const toISOString = (value) => {
  if (!value) return null;
  if (typeof value === "string") return value;
  const date = typeof value.toDate === "function" ? value.toDate() : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

/** Attach the document id and drop Firestore-only bookkeeping fields. */
export const mapDoc = (snap) => {
  if (!snap || !snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
};

export const mapQuery = (querySnap) => querySnap.docs.map((d) => ({ id: d.id, ...d.data() }));

// -------------------------------------------------------------------- caching

const CACHE_TTL_MS = 5 * 60 * 1000;
const cache = new Map();
const inFlight = new Map();

/**
 * Memoise a loader by key, de-duplicating concurrent callers.
 *
 * The header, footer and page body all ask for categories on first paint; this
 * turns that into one Firestore read instead of three.
 */
export const cached = async (key, loader, ttl = CACHE_TTL_MS) => {
  const hit = cache.get(key);
  if (hit && Date.now() - hit.at < ttl) return hit.value;

  if (inFlight.has(key)) return inFlight.get(key);

  const promise = (async () => {
    try {
      const value = await loader();
      cache.set(key, { value, at: Date.now() });
      return value;
    } finally {
      inFlight.delete(key);
    }
  })();

  inFlight.set(key, promise);
  return promise;
};

/** Drop cached reads so the CMS sees its own edits immediately. */
export const invalidateCache = (prefix) => {
  if (!prefix) {
    cache.clear();
    return;
  }
  Array.from(cache.keys())
    .filter((key) => key.startsWith(prefix))
    .forEach((key) => cache.delete(key));
};

// ------------------------------------------------------------------- fallback

/**
 * Run a Firestore read, falling back to bundled default content on any failure
 * (offline, rules rejection, unconfigured project, empty collection).
 */
export const safe = async (loader, fallback, label = "read") => {
  if (!isFirebaseConfigured) return fallback;
  try {
    const value = await loader();
    if (Array.isArray(value) && value.length === 0 && Array.isArray(fallback) && fallback.length) {
      // An empty collection almost always means "not seeded yet" rather than
      // "deliberately empty", and an empty shop window helps nobody.
      // eslint-disable-next-line no-console
      console.warn(`[services] ${label} returned no documents — using bundled content.`);
      return fallback;
    }
    return value;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(`[services] ${label} failed — using bundled content.`, error);
    return fallback;
  }
};

/** Thin wrappers so services read declaratively. */
export const fetchAll = async (query) => mapQuery(await getDocs(query));
export const fetchOne = async (ref) => mapDoc(await getDoc(ref));
export const fetchFirst = async (query) => {
  const snap = await getDocs(query);
  return snap.empty ? null : { id: snap.docs[0].id, ...snap.docs[0].data() };
};
